import type { Database, Json } from '@auctisync/shared';

import type { AuctisyncSupabase } from './client.js';
import { DataRequestError } from './request-error.js';
import { lotListingChoices } from './listing-prices.js';

/**
 * The AI draft of a lot, and what a person may do with it — ONE set of rules
 * for the office's auction screen and the Cataloging app.
 *
 * 🔴 WHY THIS LIVES HERE. Jim's rules (2026-09-23): a ready draft fills the
 * lot's BLANK descriptive fields on the server, and never a field a person
 * typed; PRICES wait for a person to accept them. Two screens act on that — the
 * office reviewing a sale and a worker holding the item — and a copy of these
 * rules in each would agree the day it was written and drift the first time
 * either changed (`feedback_no_mechanical_divergence`). The wording is shared
 * for the same reason: "Prices to review" must mean the same thing on both.
 *
 * The rules that move MONEY are not here at all. Accepting prices is one
 * database function (`accept_lot_draft_prices`) that checks the caller is
 * staff of the lot's house, refuses a live lot, and refuses a reserve below
 * the starting bid. This file only calls it and words its refusals.
 */

// ─── The fields ─────────────────────────────────────────────────────────────

export type DraftFieldGroup = 'identity' | 'specs' | 'pricing';

/** How a field is edited. `cents` is shown and typed in dollars. */
export type DraftFieldKind = 'text' | 'textarea' | 'integer' | 'cents';

/** The lot columns a draft may write. Prices only through the accept function. */
export type DraftLotColumn =
  | 'title'
  | 'description'
  | 'condition'
  | 'category_id'
  | 'quantity'
  | 'mpn'
  | 'upc'
  | 'msrp_cents'
  | 'retail_price_cents'
  | 'starting_bid_cents'
  | 'reserve_cents'
  | 'estimate_min_cents'
  | 'estimate_max_cents';

export type PriceKey =
  | 'msrp_cents'
  | 'retail_price_cents'
  | 'starting_bid_cents'
  | 'reserve_cents'
  | 'estimate_min_cents'
  | 'estimate_max_cents';

export interface DraftField {
  /**
   * Stable key. Equals the name the server's auto-fill records in
   * `autofilled_fields`, so "the AI filled this" is a lookup, not a mapping.
   */
  key: string;
  label: string;
  group: DraftFieldGroup;
  /** Where the value sits in the draft's `draft_values`. */
  draftKey: string;
  kind: DraftFieldKind;
  /** Where it lands on the lot: a column, or a key inside `custom_fields`. */
  target: { column: DraftLotColumn } | { customField: string };
}

/**
 * Every field an AI draft carries, in the order both screens show them.
 *
 * `category` is special: the draft holds a SLUG from the house's own list, the
 * lot holds an id. The server resolves it during auto-fill; a person editing it
 * picks from the house's categories.
 */
export const DRAFT_FIELDS: readonly DraftField[] = [
  {
    key: 'title',
    label: 'Title',
    group: 'identity',
    draftKey: 'title',
    kind: 'text',
    target: { column: 'title' },
  },
  {
    key: 'description',
    label: 'Description',
    group: 'identity',
    draftKey: 'description',
    kind: 'textarea',
    target: { column: 'description' },
  },
  {
    key: 'condition',
    label: 'Condition',
    group: 'identity',
    draftKey: 'condition',
    kind: 'text',
    target: { column: 'condition' },
  },
  {
    key: 'category',
    label: 'Category',
    group: 'identity',
    draftKey: 'category_slug',
    kind: 'text',
    target: { column: 'category_id' },
  },
  {
    key: 'mpn',
    label: 'MPN',
    group: 'identity',
    draftKey: 'mpn',
    kind: 'text',
    target: { column: 'mpn' },
  },
  {
    key: 'upc',
    label: 'UPC',
    group: 'identity',
    draftKey: 'upc',
    kind: 'text',
    target: { column: 'upc' },
  },
  {
    key: 'brand',
    label: 'Brand',
    group: 'specs',
    draftKey: 'brand',
    kind: 'text',
    target: { customField: 'brand' },
  },
  {
    key: 'model',
    label: 'Model',
    group: 'specs',
    draftKey: 'model',
    kind: 'text',
    target: { customField: 'model' },
  },
  {
    key: 'year',
    label: 'Year',
    group: 'specs',
    draftKey: 'year',
    kind: 'integer',
    target: { customField: 'year' },
  },
  {
    key: 'material',
    label: 'Material',
    group: 'specs',
    draftKey: 'material',
    kind: 'text',
    target: { customField: 'material' },
  },
  {
    key: 'color',
    label: 'Color',
    group: 'specs',
    draftKey: 'color',
    kind: 'text',
    target: { customField: 'color' },
  },
  {
    key: 'dimensions',
    label: 'Dimensions',
    group: 'specs',
    draftKey: 'dimensions',
    kind: 'text',
    target: { customField: 'dimensions' },
  },
  {
    key: 'msrp',
    label: 'MSRP',
    group: 'pricing',
    draftKey: 'msrp_cents',
    kind: 'cents',
    target: { column: 'msrp_cents' },
  },
  {
    key: 'retail_price',
    label: 'Retail price',
    group: 'pricing',
    draftKey: 'retail_price_cents',
    kind: 'cents',
    target: { column: 'retail_price_cents' },
  },
  {
    key: 'starting_bid',
    label: 'Starting bid',
    group: 'pricing',
    draftKey: 'starting_bid_cents',
    kind: 'cents',
    target: { column: 'starting_bid_cents' },
  },
  {
    key: 'reserve',
    label: 'Reserve',
    group: 'pricing',
    draftKey: 'reserve_cents',
    kind: 'cents',
    target: { column: 'reserve_cents' },
  },
  {
    key: 'estimate_min',
    label: 'Estimate (low)',
    group: 'pricing',
    draftKey: 'estimate_min_cents',
    kind: 'cents',
    target: { column: 'estimate_min_cents' },
  },
  {
    key: 'estimate_max',
    label: 'Estimate (high)',
    group: 'pricing',
    draftKey: 'estimate_max_cents',
    kind: 'cents',
    target: { column: 'estimate_max_cents' },
  },
];

/**
 * How many units the lot is — a PERSON's field, shown with the descriptive
 * fields but NOT in `DRAFT_FIELDS` (design round of 2026-09-29).
 *
 * The AI does not draft a quantity, and `DRAFT_FIELDS` must match the keys the
 * server's auto-fill records exactly (its test says why), so quantity sits
 * beside the list rather than in it. `lots.quantity` is NOT NULL DEFAULT 1 and
 * is also edited on the office's lot page; bidders see "×N units" above 1.
 * If the AI is ever asked to count, this moves into the list together with the
 * prompt and the auto-fill.
 */
export const QUANTITY_FIELD: DraftField = {
  key: 'quantity',
  label: 'Quantity',
  group: 'identity',
  draftKey: 'quantity',
  kind: 'integer',
  target: { column: 'quantity' },
};

/** A quantity as typed: a whole number, 1 or more; anything else is 1. */
export function parseQuantityInput(raw: string): number {
  const n = Number.parseInt(raw.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

export const PRICE_KEYS: readonly PriceKey[] = [
  'msrp_cents',
  'retail_price_cents',
  'starting_bid_cents',
  'reserve_cents',
  'estimate_min_cents',
  'estimate_max_cents',
];

export function isPriceField(f: DraftField): boolean {
  return f.group === 'pricing';
}

/** The condition values the AI chooses from, and the only ones offered. */
export const LOT_CONDITIONS = [
  'new',
  'like-new',
  'used-very-good',
  'used-good',
  'used-fair',
  'for-parts',
] as const;

/**
 * What a bidder reads for each condition code (Jim, 2026-09-23: "I want the
 * proper labels"). The AI files condition as one of `LOT_CONDITIONS`, and
 * before this every bidder page printed the code itself — "used-good".
 */
export const LOT_CONDITION_LABELS: Readonly<Record<(typeof LOT_CONDITIONS)[number], string>> = {
  new: 'New',
  'like-new': 'Like new',
  'used-very-good': 'Used – very good',
  'used-good': 'Used – good',
  'used-fair': 'Used – fair',
  'for-parts': 'For parts or repair',
};

/**
 * A lot's condition as a bidder reads it: a known code becomes its label;
 * anything else is a house's own words ("Some veneer lifting") and shows
 * exactly as typed. Null for blank.
 */
export function conditionLabel(condition: string | null | undefined): string | null {
  const v = condition?.trim();
  if (!v) return null;
  const key = v.toLowerCase() as (typeof LOT_CONDITIONS)[number];
  return Object.hasOwn(LOT_CONDITION_LABELS, key) ? LOT_CONDITION_LABELS[key] : v;
}

// ─── The draft ──────────────────────────────────────────────────────────────

export type DraftStatus = 'queued' | 'running' | 'ready' | 'failed';

export interface LotDraft {
  lot_id: string;
  status: DraftStatus;
  draft_values: Record<string, unknown> | null;
  confidence: Record<string, number> | null;
  pricing_logic: string | null;
  identification_notes: string | null;
  source_urls: string[];
  /** The lot fields THIS draft wrote — blank ones it filled, and earlier AI values it replaced. */
  autofilled_fields: string[];
  autofill_error: string | null;
  /**
   * The value AI research put in each lot field that still holds it, by field
   * key (a category as its id). A field whose value differs was changed by a
   * person, and no later run replaces it (Jim, 2026-10-05) — see `aiOwnsField`.
   */
  ai_values: Record<string, string>;
  prices_accepted_at: string | null;
  last_error: string | null;
  attempts: number;
  completed_at: string | null;
}

const DRAFT_COLUMNS =
  'lot_id, status, draft_values, confidence, pricing_logic, identification_notes, source_urls, autofilled_fields, autofill_error, ai_values, prices_accepted_at, last_error, attempts, completed_at';

interface DraftRowRaw {
  lot_id: string;
  status: string;
  draft_values: Json | null;
  confidence: Json | null;
  pricing_logic: string | null;
  identification_notes: string | null;
  source_urls: Json | null;
  autofilled_fields: string[] | null;
  autofill_error: string | null;
  ai_values?: Json | null;
  prices_accepted_at: string | null;
  last_error: string | null;
  attempts: number;
  completed_at: string | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Narrow a raw row. Unknown shapes become empty rather than throwing mid-render. */
export function toLotDraft(r: DraftRowRaw): LotDraft {
  const status: DraftStatus = (['queued', 'running', 'ready', 'failed'] as const).includes(
    r.status as DraftStatus,
  )
    ? (r.status as DraftStatus)
    : 'failed';
  const confidence: Record<string, number> = {};
  if (isRecord(r.confidence)) {
    for (const [k, v] of Object.entries(r.confidence)) if (typeof v === 'number') confidence[k] = v;
  }
  const aiValues: Record<string, string> = {};
  if (isRecord(r.ai_values)) {
    for (const [k, v] of Object.entries(r.ai_values)) if (typeof v === 'string') aiValues[k] = v;
  }
  return {
    lot_id: r.lot_id,
    status,
    draft_values: isRecord(r.draft_values) ? r.draft_values : null,
    confidence: isRecord(r.confidence) ? confidence : null,
    pricing_logic: r.pricing_logic,
    identification_notes: r.identification_notes,
    source_urls: Array.isArray(r.source_urls)
      ? r.source_urls.filter((u): u is string => typeof u === 'string')
      : [],
    autofilled_fields: r.autofilled_fields ?? [],
    autofill_error: r.autofill_error,
    ai_values: aiValues,
    prices_accepted_at: r.prices_accepted_at,
    last_error: r.last_error,
    attempts: r.attempts,
    completed_at: r.completed_at,
  };
}

/** One lot's draft, or null when the lot has never been sent to the AI. */
export async function fetchLotDraft(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<LotDraft | null> {
  const { data, error } = await supabase
    .from('lot_ai_drafts')
    .select(DRAFT_COLUMNS)
    .eq('lot_id', lotId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? toLotDraft(data as DraftRowRaw) : null;
}

/**
 * Every draft in one sale, for the office's run view. One round trip, not one
 * per lot. RLS limits it to the staff member's own house.
 */
export async function fetchAuctionDrafts(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<LotDraft[]> {
  const { data, error } = await supabase
    .from('lot_ai_drafts')
    .select(`${DRAFT_COLUMNS}, lots!inner(auction_id)`)
    .eq('lots.auction_id', auctionId);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => toLotDraft(r as unknown as DraftRowRaw));
}

// ─── Where a lot is in the pipeline — one wording for both screens ─────────

/**
 * How many times the server tries a lot before giving up. A failed draft with
 * fewer attempts than this is waiting for its next try, not finished.
 *
 * 🔴 MIRRORS `MAX_ATTEMPTS` in `drain-lot-ai-drafts`. A test reads it there.
 */
export const DRAFT_MAX_ATTEMPTS = 3;

/**
 * AI research on a lot, as the person holding it needs to know it (Jim,
 * 2026-10-05: "It is unclear to the cataloging app user that AI research is
 * being done at all"). Three states, three colours: yellow while it runs,
 * green when it is done, red when it gave up. A lot with no state has not been
 * sent yet.
 *
 * A failed attempt the server will try again is still IN PROGRESS — the
 * worker has nothing to do about it.
 */
export type ResearchState = 'in_progress' | 'complete' | 'failed';

export const RESEARCH_LABEL: Record<ResearchState, string> = {
  in_progress: 'AI research in progress',
  complete: 'AI research complete',
  failed: 'AI research didn’t finish',
};

export function researchState(
  draft: Pick<LotDraft, 'status' | 'attempts'> | null | undefined,
): ResearchState | null {
  if (!draft) return null;
  switch (draft.status) {
    case 'queued':
    case 'running':
      return 'in_progress';
    case 'ready':
      return 'complete';
    case 'failed':
      return draft.attempts < DRAFT_MAX_ATTEMPTS ? 'in_progress' : 'failed';
  }
}

export type DraftStage =
  | 'not_sent'
  | 'queued'
  | 'drafting'
  | 'ready'
  | 'prices_to_review'
  | 'failed';

/**
 * The research words, plus the one thing only the office reviews. Both screens
 * say "AI research …" for the same state — the worker's yellow and green are
 * the office's too.
 */
export const DRAFT_STAGE_LABEL: Record<DraftStage, string> = {
  not_sent: 'AI research not started',
  queued: RESEARCH_LABEL.in_progress,
  drafting: RESEARCH_LABEL.in_progress,
  ready: RESEARCH_LABEL.complete,
  prices_to_review: 'Prices to review',
  failed: RESEARCH_LABEL.failed,
};

/** The AI's suggested prices, each a whole number of cents or null. */
export function draftPrices(draft: LotDraft | null): Record<PriceKey, number | null> {
  const out = {} as Record<PriceKey, number | null>;
  for (const k of PRICE_KEYS) {
    const v = draft?.draft_values?.[k];
    out[k] = typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
  }
  return out;
}

/**
 * Where the lot is. `prices_to_review` is a READY draft whose prices nobody has
 * accepted yet — and only if the AI suggested any, so a draft with no prices
 * does not sit in the office's review list for ever.
 */
export function draftStage(draft: LotDraft | null): DraftStage {
  if (!draft) return 'not_sent';
  if (draft.status === 'queued') return 'queued';
  if (draft.status === 'running') return 'drafting';
  // Waiting for its next try is waiting, not failed (`researchState`).
  if (draft.status === 'failed') return researchState(draft) === 'failed' ? 'failed' : 'queued';
  const suggested = Object.values(draftPrices(draft)).some((v) => v !== null);
  return suggested && draft.prices_accepted_at === null ? 'prices_to_review' : 'ready';
}

// ─── Research across a sale, and asking for it again ───────────────────────

/**
 * Where AI research stands for every lot in a sale that has any, keyed by lot
 * id — the Cataloging app's "Go to a lot" list and its header line. One round
 * trip, and only the two columns the answer needs: this is polled while
 * research runs, so it must not carry every draft's text each time.
 */
export async function fetchAuctionResearch(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<Map<string, ResearchState>> {
  const { data, error } = await supabase
    .from('lot_ai_drafts')
    .select('lot_id, status, attempts, lots!inner(auction_id)')
    .eq('lots.auction_id', auctionId);
  if (error) throw new DataRequestError(error);
  const out = new Map<string, ResearchState>();
  for (const r of (data ?? []) as { lot_id: string; status: string; attempts: number }[]) {
    const state = researchState({
      status: (['queued', 'running', 'ready', 'failed'] as const).includes(r.status as DraftStatus)
        ? (r.status as DraftStatus)
        : 'failed',
      attempts: r.attempts,
    });
    if (state) out.set(r.lot_id, state);
  }
  return out;
}

export type RerunRefusal =
  | 'not_signed_in'
  | 'lot_not_found'
  | 'not_staff_of_this_house'
  | 'lot_is_live'
  | 'no_photos'
  | 'unknown';

export const RERUN_MESSAGE: Record<RerunRefusal, string> = {
  not_signed_in: 'Sign in again to run AI research.',
  lot_not_found: 'This lot has been deleted.',
  not_staff_of_this_house: 'You don’t have access to this lot.',
  lot_is_live: 'This lot is already live. AI research can’t change it while people are bidding.',
  no_photos: 'Take a photo of this lot first. AI research works from its photos.',
  unknown: 'AI research couldn’t be started. Try again.',
};

export type RerunResult =
  | { ok: true; state: 'queued' | 'already_in_progress' }
  | { ok: false; reason: RerunRefusal; message: string };

/**
 * Research a lot again (Jim, 2026-10-05). The office and the Cataloging app
 * both come through here to `public.rerun_lot_ai_research`, which decides who
 * may and when. The new run fills blank fields and replaces values the AI put
 * there before that nobody has changed — never a value a person entered, and
 * never a price. It is one paid AI call, so both screens confirm first.
 */
export async function rerunLotResearch(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<RerunResult> {
  const { data, error } = await supabase.rpc('rerun_lot_ai_research', { p_lot_id: lotId });
  if (error) {
    const code = (error.message ?? '').split(':')[0]!.trim();
    const reason: RerunRefusal =
      code in RERUN_MESSAGE && code !== 'unknown' ? (code as RerunRefusal) : 'unknown';
    return { ok: false, reason, message: RERUN_MESSAGE[reason] };
  }
  return { ok: true, state: data === 'already_in_progress' ? 'already_in_progress' : 'queued' };
}

// ─── Accepting prices — a person's decision ────────────────────────────────

export type AcceptPricesRefusal =
  | 'reserve_below_starting_bid'
  | 'estimate_low_above_high'
  | 'lot_is_live'
  | 'no_ready_draft'
  | 'not_staff_of_this_house'
  | 'invalid_price'
  | 'unknown';

export const ACCEPT_PRICES_MESSAGE: Record<AcceptPricesRefusal, string> = {
  reserve_below_starting_bid: 'The reserve can’t be lower than the starting bid.',
  estimate_low_above_high: 'The low estimate can’t be higher than the high estimate.',
  lot_is_live: 'This lot is already live. Prices can’t change while people are bidding.',
  no_ready_draft: 'There’s no finished AI draft for this lot yet.',
  not_staff_of_this_house: 'You don’t have access to this lot.',
  invalid_price: 'Prices must be whole dollar-and-cent amounts, zero or more.',
  unknown: 'The prices couldn’t be saved. Try again.',
};

/** The server raises a bare code as the message; `invalid_price: <key>` carries a suffix. */
export function acceptPricesRefusal(message: string | undefined): AcceptPricesRefusal {
  const code = (message ?? '').split(':')[0]!.trim();
  return code in ACCEPT_PRICES_MESSAGE && code !== 'unknown'
    ? (code as AcceptPricesRefusal)
    : 'unknown';
}

export type AcceptPricesResult =
  | { ok: true; applied: Partial<Record<PriceKey, number>> }
  | { ok: false; reason: AcceptPricesRefusal; message: string };

/**
 * Put the AI's prices on the lot, with any the person edited first.
 *
 * `overrides` holds only what they changed; everything else takes the AI's
 * value. The database decides whether it is allowed.
 */
export async function acceptDraftPrices(
  supabase: AuctisyncSupabase,
  lotId: string,
  overrides: Partial<Record<PriceKey, number>> = {},
): Promise<AcceptPricesResult> {
  const { data, error } = await supabase.rpc('accept_lot_draft_prices', {
    p_lot_id: lotId,
    p_overrides: overrides as Json,
  });
  if (error) {
    const reason = acceptPricesRefusal(error.message);
    return { ok: false, reason, message: ACCEPT_PRICES_MESSAGE[reason] };
  }
  const applied: Partial<Record<PriceKey, number>> = {};
  if (isRecord(data)) {
    for (const k of PRICE_KEYS) {
      const v = data[k];
      if (typeof v === 'number') applied[k] = v;
    }
  }
  return { ok: true, applied };
}

/**
 * Dollars as typed → whole cents, or null for an empty field.
 *
 * Returns `undefined` for anything that is not a sensible non-negative amount,
 * so a caller can refuse to save rather than guessing. Rounds to the nearest
 * cent, which is what a person typing "12.999" meant.
 */
export function parseDollarsInput(raw: string): number | null | undefined {
  const t = raw.replace(/[$,\s]/g, '');
  if (t === '') return null;
  if (!/^\d+(\.\d+)?$/.test(t)) return undefined;
  return Math.round(Number(t) * 100);
}

/** Cents → the string shown in an editable dollars field. */
export function centsToDollarsInput(cents: number | null | undefined): string {
  return typeof cents === 'number' ? (cents / 100).toFixed(2) : '';
}

// ─── A person editing the descriptive fields ───────────────────────────────

export interface LotDescriptivePatch {
  title?: string | null;
  description?: string | null;
  condition?: string | null;
  category_id?: string | null;
  /** Units in the lot — a whole number, 1 or more (`lots.quantity` is NOT NULL). */
  quantity?: number;
  mpn?: string | null;
  upc?: string | null;
  /** Spec keys to set; `null` removes the key. Other specs are left alone. */
  specs?: Record<string, string | number | null>;
}

/**
 * Save a person's edits to a lot's descriptive fields.
 *
 * 🔴 SPECS MERGE, THEY DO NOT REPLACE. `custom_fields` is read fresh
 * immediately before the write, so a spec the server's auto-fill wrote a moment
 * ago — or the office typed from another screen — is not wiped by a worker who
 * only changed the colour. Empty strings are stored as null: a field a person
 * cleared is blank, and blank is what auto-fill looks for.
 */
export async function saveLotDescriptiveFields(
  supabase: AuctisyncSupabase,
  lotId: string,
  patch: LotDescriptivePatch,
): Promise<void> {
  const clean = (v: string | null | undefined) =>
    v === undefined ? undefined : v === null || v.trim() === '' ? null : v.trim();

  const update: Database['public']['Tables']['lots']['Update'] = {};
  for (const k of ['title', 'description', 'condition', 'mpn', 'upc'] as const) {
    const v = clean(patch[k]);
    if (v !== undefined) update[k] = v;
  }
  if (patch.category_id !== undefined) update.category_id = patch.category_id;
  if (patch.quantity !== undefined) {
    update.quantity = Number.isInteger(patch.quantity) && patch.quantity >= 1 ? patch.quantity : 1;
  }

  if (patch.specs && Object.keys(patch.specs).length > 0) {
    const { data, error } = await supabase
      .from('lots')
      .select('custom_fields')
      .eq('id', lotId)
      .single();
    if (error) throw new DataRequestError(error);
    const merged: Record<string, Json | undefined> = isRecord(data.custom_fields)
      ? { ...(data.custom_fields as Record<string, Json | undefined>) }
      : {};
    for (const [k, v] of Object.entries(patch.specs)) {
      const cleaned = typeof v === 'string' ? clean(v) : v;
      if (cleaned === null || cleaned === undefined) delete merged[k];
      else merged[k] = cleaned;
    }
    update.custom_fields = merged;
  }

  if (Object.keys(update).length === 0) return;
  const { error } = await supabase.from('lots').update(update).eq('id', lotId);
  if (error) throw new DataRequestError(error);
}

// ─── The capture record — the worker's note and the inventory flag ─────────

export interface LotCapture {
  lot_id: string;
  notes: string | null;
  send_to_inventory: boolean;
}

/** The capture record for many lots at once, keyed by lot id. */
export async function fetchLotCaptures(
  supabase: AuctisyncSupabase,
  lotIds: string[],
): Promise<Map<string, LotCapture>> {
  const out = new Map<string, LotCapture>();
  if (lotIds.length === 0) return out;
  const { data, error } = await supabase
    .from('lot_capture')
    .select('lot_id, notes, send_to_inventory')
    .in('lot_id', lotIds);
  if (error) throw new Error(error.message);
  for (const r of data ?? []) out.set(r.lot_id, r);
  return out;
}

/** The longest note the database accepts. */
export const CAPTURE_NOTE_MAX = 2000;

/**
 * Save the worker's note and/or the inventory flag. An upsert: the first save
 * creates the record. The house is taken from the LOT by the database, whatever
 * `tenantId` says — it is only here because the insert shape requires it.
 */
export async function saveLotCapture(
  supabase: AuctisyncSupabase,
  lotId: string,
  tenantId: string,
  patch: { notes?: string | null; send_to_inventory?: boolean },
): Promise<void> {
  const row: {
    lot_id: string;
    tenant_id: string;
    notes?: string | null;
    send_to_inventory?: boolean;
  } = {
    lot_id: lotId,
    tenant_id: tenantId,
  };
  if (patch.notes !== undefined) {
    const n = patch.notes?.trim() ?? '';
    row.notes = n === '' ? null : n.slice(0, CAPTURE_NOTE_MAX);
  }
  if (patch.send_to_inventory !== undefined) row.send_to_inventory = patch.send_to_inventory;
  const { error } = await supabase.from('lot_capture').upsert(row, { onConflict: 'lot_id' });
  if (error) throw new DataRequestError(error);
}

/**
 * The worker has finished a lot.
 *
 * 🔴 THIS IS WHAT QUEUES THE AI, and the database only queues a lot that
 * already HAS photos when this lands. A caller must therefore write this after
 * the lot's photos have uploaded — never on the swipe itself, which on
 * warehouse wifi routinely comes first. The Cataloging app's capture queue
 * orders it behind the photos for exactly that reason.
 *
 * Writing it again later (the worker came back and added photos) re-queues the
 * lot and supersedes the old draft, by design.
 */
export async function markCaptureComplete(
  supabase: AuctisyncSupabase,
  lotId: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('lots')
    .update({
      capture_completed_at: new Date().toISOString(),
      capture_completed_by_user_id: userId,
    })
    .eq('id', lotId);
  if (error) throw new DataRequestError(error);
}

// ─── Reads for the review screens ──────────────────────────────────────────

/** A lot's editable fields, as a person reviewing its draft sees them. */
export interface LotForReview {
  id: string;
  tenant_id: string;
  status: string;
  title: string | null;
  description: string | null;
  condition: string | null;
  category_id: string | null;
  quantity: number;
  mpn: string | null;
  upc: string | null;
  custom_fields: Record<string, unknown>;
  msrp_cents: number | null;
  retail_price_cents: number | null;
  starting_bid_cents: number;
  reserve_cents: number | null;
  estimate_min_cents: number | null;
  estimate_max_cents: number | null;
  /** "Show on listing" — which prices a bidder sees (`listing-prices.ts`). */
  show_msrp: boolean;
  show_retail_price: boolean;
  show_estimate: boolean;
}

/**
 * The lot as staff review it — including the reserve, which is why this is a
 * STAFF read and not the public lot query (`PUBLIC_LOT_COLUMNS` excludes it).
 */
export async function fetchLotForReview(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<LotForReview | null> {
  // lots_staff, not lots: reserve, estimates and retail are hidden from the
  // shared authenticated role on public.lots (20260923210000). The view is
  // owned by postgres and row-limited to the caller's own house.
  const { data, error } = await supabase
    .from('lots_staff')
    .select(
      'id, tenant_id, status, title, description, condition, category_id, quantity, mpn, upc, custom_fields, msrp_cents, retail_price_cents, starting_bid_cents, reserve_cents, estimate_min_cents, estimate_max_cents, show_msrp, show_retail_price, show_estimate',
    )
    .eq('id', lotId)
    .maybeSingle();
  if (error) throw new DataRequestError(error);
  if (!data) return null;
  // A view reports every column as nullable; the boxes never are on the table.
  const choices = lotListingChoices(data);
  return {
    ...data,
    custom_fields: isRecord(data.custom_fields) ? data.custom_fields : {},
    show_msrp: choices.msrp,
    show_retail_price: choices.retail_price,
    show_estimate: choices.estimate,
  };
}

export interface HouseCategory {
  id: string;
  slug: string;
  name: string;
}

/**
 * The house's live categories — the only ones a draft can be filed under.
 * Archived ones are left out, matching what the AI is offered and what the
 * server's auto-fill will accept.
 */
export async function fetchHouseCategories(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<HouseCategory[]> {
  const { data, error } = await supabase
    .from('item_categories')
    .select('id, slug, name')
    .eq('tenant_id', tenantId)
    .is('archived_at', null)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new DataRequestError(error);
  return data ?? [];
}

/**
 * The rating at or above which the server writes a draft value onto a blank
 * lot field by itself (`private.draft_field_is_sure`, 20260923170000). Jim,
 * 2026-09-23, after a 60/100 guess filed a drill insert under "Collectibles".
 *
 * 🔴 MIRRORS SQL. A test reads the migration and fails if the two disagree.
 */
export const AUTOFILL_CONFIDENCE_FLOOR = 70;

/** A draft value the server did NOT write onto the lot, because the AI was not sure enough. */
export interface UnsureSuggestion {
  field: DraftField;
  /** As a person reads it — a category's NAME, not its slug. */
  value: string;
  /** The AI's 0-100 rating, or null when the draft carried none. */
  confidence: number | null;
}

/**
 * Draft values waiting for a person: a DESCRIPTIVE field that is still blank,
 * that the draft has a value for, and that auto-fill did not write.
 *
 * Without this list a below-floor value would be stored on the draft and shown
 * nowhere — neither screen showed a descriptive draft value that auto-fill had
 * not written. The floor exists so a person decides; this is what lets them.
 *
 * `isBlank` is how the caller's own copy of the lot reads: the app passes the
 * lot as last read, the office passes its open form — which is what a person
 * is about to save.
 */
export function unsureSuggestions(
  draft: LotDraft | null,
  isBlank: (field: DraftField) => boolean,
  categories: HouseCategory[],
): UnsureSuggestion[] {
  if (!draft || draft.status !== 'ready') return [];
  const filled = new Set(draft.autofilled_fields);
  const out: UnsureSuggestion[] = [];
  for (const field of DRAFT_FIELDS) {
    if (field.group === 'pricing' || filled.has(field.key)) continue;
    const value = draftFieldValue(draft, field, categories);
    if (value === '' || !isBlank(field)) continue;
    const c = draft.confidence?.[field.draftKey];
    out.push({ field, value, confidence: typeof c === 'number' ? c : null });
  }
  return out;
}

/**
 * The save that takes one suggestion onto the lot, for `saveLotDescriptiveFields`.
 * Null when the value cannot be placed — a category slug the house no longer has.
 */
export function suggestionPatch(
  draft: LotDraft,
  field: DraftField,
  categories: HouseCategory[],
): LotDescriptivePatch | null {
  const raw = draft.draft_values?.[field.draftKey];
  if (raw === null || raw === undefined || raw === '') return null;
  if (field.key === 'category') {
    const id = categories.find((c) => c.slug === raw)?.id;
    return id ? { category_id: id } : null;
  }
  if ('customField' in field.target) {
    return typeof raw === 'string' || typeof raw === 'number'
      ? { specs: { [field.target.customField]: raw } }
      : null;
  }
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const col = field.target.column;
  if (
    col === 'title' ||
    col === 'description' ||
    col === 'condition' ||
    col === 'mpn' ||
    col === 'upc'
  ) {
    return { [col]: String(raw) };
  }
  return null;
}

/** A value from a lot, read the way a field of this kind is edited. */
export function lotFieldValue(
  lot: LotForReview,
  field: DraftField,
  categories: HouseCategory[],
): string {
  if ('customField' in field.target) {
    const v = lot.custom_fields[field.target.customField];
    return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
  }
  const col = field.target.column;
  if (col === 'category_id') {
    return categories.find((c) => c.id === lot.category_id)?.name ?? '';
  }
  const v = lot[col];
  if (field.kind === 'cents') return centsToDollarsInput(typeof v === 'number' ? v : null);
  return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}

/**
 * Whether the lot's value in this field is still exactly what AI research put
 * there — the "AI" mark beside a field. Compared with the lot as it is NOW, so
 * a field a person has since changed stops being the AI's at once, on both
 * screens, with no writer having to say so. Mirrors the server's rule
 * (`private.ai_may_write`): trimmed text, a category by id.
 */
export function aiOwnsField(draft: LotDraft | null, lot: LotForReview, field: DraftField): boolean {
  const ai = draft?.ai_values[field.key];
  if (ai === undefined) return false;
  let raw: unknown;
  if ('customField' in field.target) raw = lot.custom_fields[field.target.customField];
  else raw = lot[field.target.column];
  if (typeof raw !== 'string' && typeof raw !== 'number') return false;
  return String(raw).trim() === ai;
}

/** A value from the AI draft, formatted the same way. */
export function draftFieldValue(
  draft: LotDraft | null,
  field: DraftField,
  categories: HouseCategory[],
): string {
  const v = draft?.draft_values?.[field.draftKey];
  if (v === null || v === undefined) return '';
  if (field.key === 'category') {
    return categories.find((c) => c.slug === v)?.name ?? '';
  }
  if (field.kind === 'cents') return typeof v === 'number' ? centsToDollarsInput(v) : '';
  return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}
